import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { AppIconButton, AppPage } from '../../components/design-system';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { tenantDevicesService } from '../../services/TenantDevicesService';
import { efemService } from '../../services/EfemService';
import * as dt from '@cnapse/shared/data.types.js';
import { PRODUCT_NAMES } from '@cnapse/shared';
import { EfemGatewayList, type EfemGatewayEntry } from '../../components/efem/setup/EfemGatewayList';
import { EfemGatewayListPanel } from '../../components/efem/setup/EfemGatewayListPanel';
import { EfemGatewayDetail } from '../../components/efem/setup/EfemGatewayDetail';
import { EfemGatewaySplitLayout } from '../../components/efem/setup/EfemGatewaySplitLayout';

function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function getGatewaySnFromPath(): string | null {
  const parts = window.location.pathname.split('/');
  const lastPart = parts[parts.length - 1];
  if (!lastPart || lastPart === 'efem-setup') return null;
  return decodeURIComponent(lastPart);
}

export default function EfemSetupPage() {
  const [gateways, setGateways] = useState<EfemGatewayEntry[]>([]);
  const [registeredSns, setRegisteredSns] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSn, setSelectedSn] = useState<string | null>(getGatewaySnFromPath);

  const loadGateways = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [efemRes, devicesRes] = await Promise.all([
        efemService.getEfemClients(),
        tenantDevicesService.getTenantDevices({ limit: 200, deviceCategory: 'efem-gateway' }),
      ]);

      const tenantAssignedGateways = devicesRes.data.devices.filter(
        (d) => d.deviceCategory === 'efem-gateway',
      );
      const tenantGatewaySns = new Set(tenantAssignedGateways.map((d) => d.serialNumber));

      const registered: EfemGatewayEntry[] = efemRes.clients
        .filter(
          (c) => !!c.gatewaySerialNumber && tenantGatewaySns.has(c.gatewaySerialNumber),
        )
        .map((c) => ({
          sn: c.gatewaySerialNumber!,
          name: c.name,
          status: c.status,
          inventoryStatus: 'active' as const,
          isRegistered: true,
        }));

      const registeredSnSet = new Set(registered.map((e) => e.sn));
      setRegisteredSns(registeredSnSet);

      const unregistered: EfemGatewayEntry[] = tenantAssignedGateways
        .filter((d) => !registeredSnSet.has(d.serialNumber))
        .map((d) => ({
          sn: d.serialNumber,
          name: d.deviceModel ?? d.serialNumber,
          status: dt.ONLINE_OFFLINE_STATUS.OFFLINE,
          inventoryStatus: d.inventoryStatus ?? 'claimed',
          isRegistered: false,
        }));

      setGateways([...registered, ...unregistered]);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to load ${PRODUCT_NAMES.AXON} gateways`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGateways();
  }, [loadGateways]);

  useEffect(() => {
    if (!selectedSn && gateways.length > 0) {
      const first = gateways.find((g) => g.isRegistered) ?? gateways[0];
      setSelectedSn(first.sn);
      navigateTo(`/devices/efem-setup/${encodeURIComponent(first.sn)}`);
    }
  }, [gateways, selectedSn]);

  const handleSelect = (sn: string) => {
    setSelectedSn(sn);
    navigateTo(`/devices/efem-setup/${encodeURIComponent(sn)}`);
  };

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadGateways()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadGateways, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <AppPage sx={{ flex: 1, minHeight: 0, gap: 0 }}>
      {error && (
        <Alert severity="error" sx={{ flexShrink: 0, mt: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', mt: error ? 1 : 0 }}>
        <EfemGatewaySplitLayout
          mobileShowPanel={!!selectedSn}
          list={
            <EfemGatewayListPanel count={gateways.length} loading={loading}>
              <EfemGatewayList
                gateways={gateways}
                selectedSn={selectedSn}
                onSelect={handleSelect}
                loading={false}
              />
            </EfemGatewayListPanel>
          }
          panel={
            !selectedSn ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, p: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  Select a gateway from the list to begin setup.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
                <EfemGatewayDetail
                  gatewaySerialNumber={selectedSn}
                  apiMode="tenant"
                  isUnregistered={!registeredSns.has(selectedSn)}
                  inventoryStatus={gateways.find((g) => g.sn === selectedSn)?.inventoryStatus ?? 'claimed'}
                  onRefresh={loadGateways}
                />
              </Box>
            )
          }
        />
      </Box>
    </AppPage>
  );
}
